import JsonResponse from "csi/comm/JsonResponse";
import { ComponentItemDataStore } from "designSystem/utilities/component-item-functions";
import { ILocaleProvider } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import { EMPTY_URL } from "csi/constants";
import NodeDataStore from "csi/datastore/NodeDataStore";
import locale from "csi/locale";
import csi from "csi/main";
import SearchManager from "csi/page/SearchManager";
import { confirmLeavePage } from "csi/router";
import { getSearch } from "csi/service/dataStoreService";
import { submitWriter } from "csi/service/writerService";
import { ReactionStore } from "csi/utility/mobxUtility";
import WorkingCache from "csi/state/WorkingCache";
import { C8Node, cnl, ICache } from "csi/typings";
import { pathCreate } from "csi/utility/pathUtility";
import xml from "csi/xml";
import Search from "csi/xml/Search";
import Writer from "csi/xml/Writer";
import _ from "lodash";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { createContext, useContext } from "react";
import ProductType from "src/Apparel/ProductType";
import ProductGalleryStore from "../NewProductPresentation/ProductGalleryStore";
import { BASEPRODUCT_PRESENTATION_SET_ITEMS, COLOR_SPECIFICATION, ColorComponentItem, defaultColorUsedInPresentation, FOOD, getProductColorSpecImage, SET_ITEM_PRODUCT, SET_ITEMS } from "../PPService";
import { IPresentationProps } from "../PresentationPageStore";

/** resolution actions */
const REPLACE_ALL = "0";
const REPLACE_SEPARATELY = "1";
const REMOVE_ALL = "2";
const KEEP_ALL = "3";

// operation parameters
const PP_SET_URLS = "PPSetUrls";
const PP_RESOLVED_SET_ITEM_PRODUCT_URL = "PPResolvedSetItemProductUrl";
const PP_RESOLVED_SET_ITEM_PRODUCTCOLOR_URL = "PPResolvedSetItemProductColorUrl";
const PP_SET_ITEM_PRODUCT_URL = "PPSetItemProductUrl";
const PP_SET_ITEM_PRODUCTCOLOR_URL = "PPSetItemProductColorUrl";

class ProductColorItem {
	@observable private _colorItemUrl: string;
	private readonly _productUrl: string;

	/** node cache */
	readonly cache: ICache = new WorkingCache();

	/** properties of page store */
	readonly props: IPresentationProps;

	/** store for the product */
	@computed get colorDataStore(): ComponentItemDataStore<ComponentItem> | null {
		const { product, cache, props, productUrl } = this;

		const { colorQuery, primaryType, productColorType } = props;
		const colorUrls = product?.ProductColors;

		if (!colorUrls || colorUrls.length === 0) {
			return null;
		}

		// get color spec
		const _getColorSpec = (color: C8Node): ColorComponentItem => {
			const href = getProductColorSpecImage(color, cache);
			const name = locale.getNodeConfiguredName(color, cache);

			return {
				href,
				id: color.$URL,
				name,
			};
		};

		return new ComponentItemDataStore(null, async () => {
			const search = getSearch(`${productColorType}.${primaryType}`, product.$URL);
			search.addDependencyPaths([pathCreate("ColorSpecification")]);
			search.getQuery().importQuery(colorQuery);
			search.getSortQuery().SortByNode(xml.PROP_NAME);

			const store = new NodeDataStore(search, cache);
			const total = await store.load();
			const validColors: C8Node[] = [];

			for (let i = 0; i < total; ++i) {
				validColors.push(store.get(i));
			}

			const colors: ColorComponentItem[] = [];
			const defaultColor: ComponentItem = {
				id: productUrl,
				name: "",
				res: "Default",
			};

			colors.push(defaultColor);

			for (const color of validColors) {
				colors.push(_getColorSpec(color));
			}

			_.sortBy(colors, ["name"]);

			return colors;
		});
	}

	/** url of color */
	get colorItemUrl(): string {
		return this._colorItemUrl;
	}

	/** url of product */
	get productUrl(): string {
		return this._productUrl;
	}

	/** the product node */
	get product(): C8Node {
		return this.cache.get(this.productUrl);
	}

	/** select color */
	@action setColorItemUrl = (newColorItemUrl: string): void => {
		this._colorItemUrl = newColorItemUrl;
	};

	constructor(productUrl: string, props: IPresentationProps, cache: ICache) {
		makeObservable(this);
		this.cache = cache;
		this.props = props;
		this._productUrl = productUrl;

		// make the product as the default color
		this._colorItemUrl = productUrl;
	}
}

export class ResolutionColorItem {
	@observable private _actionIndex: string = REPLACE_ALL;
	@observable.ref private _productColorItem: ProductColorItem;
	private readonly _colorUrl: cnl;
	private readonly _setUrls: cnl[];

	/** action options */
	readonly actionOptions: ComponentItem[];

	/** node cache */
	readonly cache: ICache = new WorkingCache();

	/** properties of page store */
	readonly props: IPresentationProps;

	/** the index of the selected action on this color */
	get actionIndex(): string {
		return this._actionIndex || REPLACE_ALL;
	}

	/** color node */
	get color(): C8Node {
		const { cache } = this;
		return cache.get(this._colorUrl);
	}

	/** get the image of the color */
	@computed get colorImage(): string | null {
		const { cache, color } = this;
		return getProductColorSpecImage(color, cache);
	}

	/** if this color is an empty color */
	get isEmptyColor(): boolean {
		const { color } = this;

		return xml.isEmptyURL(color?.$URL);
	}

	/** the url of resolved item, if the color is null, that means the current color is "Default" color, make it null then */
	@computed get url(): string {
		const { color } = this;
		return color?.$URL || EMPTY_URL;
	}

	/** product color combination on the color level */
	get productColorItem(): ProductColorItem {
		return this._productColorItem;
	}

	/** set nodes */
	get sets(): C8Node[] {
		const { cache } = this;
		return cache.getNodes(this._setUrls);
	}

	/** urls of the sets */
	get setUrls(): cnl[] {
		return this._setUrls;
	}

	/** indicate whether or not allow to show the component so that user can be able to pick product and color */
	get showComponentForColor(): boolean {
		const { actionIndex } = this;

		return actionIndex === REPLACE_ALL;
	}

	/** set the index of the action */
	@action setActionIndex = (item: ComponentItem): void => {
		this._actionIndex = item.id;
	};

	@action selectProduct = (url: string): void => {
		const { cache, props } = this;
		this._productColorItem = new ProductColorItem(url, props, cache);
	};

	constructor(color: C8Node, sets: C8Node[], actionOptions: ComponentItem[], props: IPresentationProps, cache: ICache) {
		makeObservable(this);
		this.cache = cache;
		this.actionOptions = actionOptions;
		this._colorUrl = color.$URL;
		this._setUrls = sets.map((set) => set.$URL);
		this.props = props;
	}
}

class PPValidationStore extends ReactionStore {
	@observable private _activeCardUrl: cnl;
	@observable.ref private _dataStore: NodeDataStore;
	@observable.ref private _galleryStore: ProductGalleryStore;
	@observable private _hasChanges = false;
	@observable.ref private _invalidColorItems: ResolutionColorItem[];
	@observable private _open: boolean;
	@observable.ref private _productColorItem: ProductColorItem;
	@observable private _topLevelActionIndex: string = REPLACE_ALL;

	/** node cache */
	readonly cache: ICache = new WorkingCache();

	/** properties of page store */
	readonly props: IPresentationProps;

	/** the index of active card */
	get activeCardUrl(): cnl {
		const { store } = this;
		return this._activeCardUrl || store.get(0)?.$URL;
	}

	/** the product being selected */
	get activeProduct(): C8Node {
		return this.cache.get(this.activeCardUrl);
	}

	/** all resolution items */
	@computed get allOptionItems(): ComponentItem[] {
		const { primaryType, productColorType } = this.props;

		return [
			{ id: REPLACE_ALL, res: productColorType ? "PresentationResolutionOptionReplaceAll" : "PresentationResolutionOptionReplaceAllForFood", resParams: [primaryType, productColorType] },
			{ id: REPLACE_SEPARATELY, res: "PresentationResolutionOptionReplaceSeparately", resParams: [productColorType] },
			{ id: REMOVE_ALL, res: "PresentationResolutionOptionRemoveAll" },
			{ id: KEEP_ALL, res: "PresentationResolutionOptionKeepAll" },
		];
	}

	/** default color node */
	@computed get defaultColor(): C8Node {
		return xml.nodeCreateClientNode(csi.EMPTY_URL);
	}

	/** indicate if disable the separate option */
	@computed get disableSeparateOption(): boolean {
		return this.invalidColorItems?.length <= 1;
	}

	/** the store for the card */
	get galleryStore(): ProductGalleryStore {
		return this._galleryStore;
	}

	/** indicate if user makes changes to database */
	get hasChanges(): boolean {
		return this._hasChanges;
	}

	/** invalid color items of the selected product */
	get invalidColorItems(): ResolutionColorItem[] {
		return this._invalidColorItems;
	}

	/** indicate whether or not is the last one in the list */
	get isTheLastItem(): boolean {
		return this.store?.totalCount === 1;
	}

	/** indicate if the validation center dialog is open or not */
	get open(): boolean {
		return this._open;
	}

	/** product card node store */
	get productCardNodeStore(): NodeDataStore {
		const { cache, props } = this;
		const { productCardSearch } = props;

		const store = new NodeDataStore(productCardSearch, cache);
		return store;
	}

	/** product color combination on the global level */
	get productColorItem(): ProductColorItem {
		return this._productColorItem;
	}

	/** options for the color level actions */
	@computed get resolutionColorItems(): ComponentItem[] {
		const { allOptionItems } = this;
		return allOptionItems.filter((item) => item.id !== REPLACE_SEPARATELY);
	}

	/** options for the top level actions */
	@computed get resolutionItems(): ComponentItem[] {
		const { allOptionItems, disableSeparateOption, resolutionColorItems } = this;

		return disableSeparateOption ? resolutionColorItems : allOptionItems;
	}

	/** search for sets */
	@computed get searchForSets(): Search {
		const { presentation } = this.props;
		const search = getSearch("ProductPresentationSet.ProductPresentation", presentation.$URL);
		search.addDependencyPaths([pathCreate("Images")]);

		return search;
	}

	/** indicate whether or not show the actions on the color level */
	get showActionToReplaceIndividually(): boolean {
		const { topLevelActionIndex } = this;
		return topLevelActionIndex === REPLACE_SEPARATELY;
	}

	/** indicate whether or not show the component to be able to pick another product globally */
	get showActionToReplaceProduct(): boolean {
		const { topLevelActionIndex } = this;
		return topLevelActionIndex === REPLACE_ALL;
	}

	/** generate data store for the products */
	get store(): NodeDataStore {
		return this._dataStore;
	}

	/** indicate if the selected product support color */
	get supportColor(): boolean {
		return ProductType.hasColor(this.activeProduct);
	}

	/** the index of the top level action */
	get topLevelActionIndex(): string {
		return this._topLevelActionIndex;
	}

	/** cancel the dialog */
	@action cancelDialog = (): void => {
		this.reset();
	};

	/** reset  */
	@action reset = (): void => {
		this._activeCardUrl = null;
		this._open = false;
	};

	/** set the index of active card */
	@action setActiveCardUrl = async (url: cnl, ignoreChanges?: boolean): Promise<void> => {
		const { productColorItem, invalidColorItems, topLevelActionIndex } = this;
		const contentChanges = invalidColorItems?.some((item) => !!item.productColorItem?.productUrl) || productColorItem?.productUrl || topLevelActionIndex !== REPLACE_ALL;

		// if content changes, and user wanna go away, popup a dialog to double check
		if (contentChanges && !ignoreChanges) {
			try {
				await confirmLeavePage();
			} catch (ex) {
				return;
			}
		}

		const { cache, calculateInvalidColors, searchForSets, props, resolutionColorItems, store } = this;
		const { presentation } = props;

		searchForSets.getQuery().RefAttr(SET_ITEM_PRODUCT, xml.OP_EQUALS, url, pathCreate(SET_ITEMS));
		const response = await searchForSets.execute();

		runInAction(() => {
			cache.cacheResponse(response);
			const activeProduct = cache.get(url);
			const sets = presentation.Sets;

			// collect the items of those colors for this product 
			const items = cache.getNodes(activeProduct.PPSetItems);
			const invalidColors = calculateInvalidColors(activeProduct);

			const invalidColorItems = [];

			for (const invalidColor of invalidColors) {
				const { $URL } = invalidColor;
				const setUrlsOfThisColor = items
					.filter(({ PPIProductColor, __Parent__ }) => PPIProductColor === $URL && sets.includes(__Parent__))
					.map((item) => item.__Parent__);

				const urls = new Set(setUrlsOfThisColor);
				const invalidColorItem = new ResolutionColorItem(invalidColor, cache.getNodes(Array.from(urls)), resolutionColorItems, props, cache);
				invalidColorItems.push(invalidColorItem);
			}

			this._activeCardUrl = url;
			this._invalidColorItems = invalidColorItems;
			this._topLevelActionIndex = REPLACE_ALL;
			this._productColorItem = null;
		});
	};

	/** hide/show the dialog */
	@action setOpen = (open: boolean): void => {
		this._open = open;
		this.initialize();
		this._hasChanges = false;
	};

	/** handler for top level action selection */
	@action selectTopLevelAction = (item: ComponentItem): void => {
		const { id } = item;
		this._topLevelActionIndex = id;
	};

	/** set the url of new product on the global level */
	@action setGlobalProductUrl = (url: string): void => {
		const { props, cache } = this;
		this._productColorItem = new ProductColorItem(url, props, cache);
	};

	/** calculate the invalid colors */
	calculateInvalidColors = (activeProduct: C8Node): C8Node[] => {
		const { cache, props, defaultColor } = this;
		const { violationColorUrls, violationUrlsOfProduct, presentation } = props;
		let invalidColorNodes = [];

		if (activeProduct) {
			const productHasViolation = violationUrlsOfProduct.has(activeProduct.$URL);
			let allColorHasViolation = false;

			if (productHasViolation) {
				// violation due to product, only show the used ones
				invalidColorNodes = invalidColorNodes.concat(cache.getNodes(activeProduct.ProductColors)?.filter((color) => color.Presentations.includes(presentation.$URL)));
			} else {
				// violation due to color
				const invalidColorUrls = activeProduct.ProductColors?.filter((url) => violationColorUrls.has(url));
				allColorHasViolation = invalidColorUrls?.length === activeProduct.ProductColors?.length;

				invalidColorNodes = invalidColorNodes.concat(cache.getNodes(invalidColorUrls));
			}

			// include the default one as needed
			if (productHasViolation || allColorHasViolation) {
				const defaultIsUsedInSetUnderTheCurrentPP = defaultColorUsedInPresentation(cache, presentation, activeProduct);

				if (defaultIsUsedInSetUnderTheCurrentPP) {
					invalidColorNodes.unshift(defaultColor);
				}
			}
		}

		return invalidColorNodes;
	};

	/** determine whether or not disable the color dropdown */
	disableColor = (productUrl: string): boolean => {
		const { cache } = this;
		const product = cache.get(productUrl);
		const supportColor = ProductType.hasColor(product);

		if (!supportColor || !product.ProductColors.length) {
			return true;
		}

		return false;
	};

	/** generate stores for the products */
	initialize(): void {
		const search: Search = new Search();
		const { props } = this;
		const {
			colorQuery,
			presentation,
			primaryType,
			violationUrls,
		} = props;

		for (const cnl of violationUrls) {
			search.setLimitUrl(cnl);
		}

		search.addDependencyPaths([
			pathCreate("ProductColors", COLOR_SPECIFICATION),
			pathCreate(BASEPRODUCT_PRESENTATION_SET_ITEMS),
		]);

		SearchManager.getSearchTypeSortQuery(primaryType, false, search.getSortQuery());

		this._dataStore = new NodeDataStore(search, this.cache);
		this._galleryStore = new ProductGalleryStore(this._dataStore, false, colorQuery, presentation.$URL);
	}

	/** determine how to render the text in the text box of product */
	getDisplayTextForProduct = (productUrl: string, loc: ILocaleProvider): string => {
		const { cache } = this;
		const node = cache.get(productUrl);

		return node ? loc.getNodeName(node) : "";
	};

	/** determine how to render the text in the text box of color */
	getDisplayTextForColor = (colorUrl: string, loc: ILocaleProvider): string => {
		const { cache, props } = this;
		const { primaryType } = props;

		if (cache.get(colorUrl)?.$Type === primaryType) {
			return loc.resource("Default");
		}

		return this.getDisplayTextForProduct(colorUrl, loc);
	};

	/** resolve the product and move on to the next  */
	@action resolveProduct = async (close?: boolean): Promise<boolean> => {
		const {
			activeCardUrl,
			cache,
			invalidColorItems,
			productColorItem,
			props,
			reset,
			store,
			topLevelActionIndex,
		} = this;

		const { primaryType } = props;

		const ppColorUrls: cnl[] = [];
		const ppSetUrls: Array<string[]> = [];
		const ppResolvedSetItemProductUrls: cnl[] = [];
		const ppResolvedSetItemProductColorUrls: cnl[] = [];
		let keepViolation = false;
		let response: JsonResponse = null;

		/** build parameters to send to server */
		const _buildParams = (item: ResolutionColorItem, productUrl: string, colorUrl: string): void => {
			let resolvedColorUrl = colorUrl;

			if (productUrl === colorUrl) {
				// it means that user selects the default one
				resolvedColorUrl = EMPTY_URL;
			}

			// if the color is null, that means the current color is "Default" color
			const originalColorUrl = item.url;

			ppColorUrls.push(originalColorUrl);
			ppSetUrls.push(item.setUrls);
			ppResolvedSetItemProductUrls.push(productUrl);
			ppResolvedSetItemProductColorUrls.push(resolvedColorUrl);
		};

		// step 1 collect the resolution items
		if (KEEP_ALL === topLevelActionIndex) {
			// don't need send to server at all
		} else if (REMOVE_ALL === topLevelActionIndex) {
			for (const item of invalidColorItems) {
				_buildParams(item, EMPTY_URL, EMPTY_URL);
			}
		} else if (REPLACE_SEPARATELY === topLevelActionIndex) {
			for (const item of invalidColorItems) {
				const { actionIndex } = item;

				if (actionIndex === REMOVE_ALL) {
					_buildParams(item, EMPTY_URL, EMPTY_URL);
				} else if (actionIndex === KEEP_ALL) {
					keepViolation = true;
					continue;
				} else {
					const resolvedItem = item.productColorItem;

					// resolvedItem won't initialized until select the product, so, it could be undefined here
					const { productUrl, colorItemUrl } = resolvedItem || {};

					if (!productUrl) {
						// doesn't pick any product yet, then skip it, which means this item still has violation left
						keepViolation = true;
						continue;
					}

					_buildParams(item, productUrl, colorItemUrl);
				}
			}
		} else if (REPLACE_ALL === topLevelActionIndex) {
			const { productUrl, colorItemUrl } = productColorItem || {};

			// if nothing picked, nothing happens
			if (productUrl) {
				for (const item of invalidColorItems) {
					_buildParams(item, productUrl, colorItemUrl);
				}
			}
		}

		// step 2, update server if needed
		if (ppColorUrls.length > 0) {
			const writer = new Writer();
			const isFood = primaryType === FOOD;

			if (isFood) {
				writer.callMethod("ProductPresentation", "EditPresentationSetForProduct")
					.addParameter(PP_SET_ITEM_PRODUCT_URL, xml.ATTR_REF, activeCardUrl)
					.addParameter(PP_SET_URLS, xml.ATTR_REFLIST, ppSetUrls[0])
					.addParameter(PP_RESOLVED_SET_ITEM_PRODUCT_URL, xml.ATTR_REF, ppResolvedSetItemProductUrls[0]);
			} else {
				for (let i = 0; i < ppColorUrls.length; i++) {
					writer.callMethod("ProductPresentation", "EditPresentationSetForColor")
						.addParameter(PP_SET_ITEM_PRODUCT_URL, xml.ATTR_REF, activeCardUrl)
						.addParameter(PP_SET_ITEM_PRODUCTCOLOR_URL, xml.ATTR_REF, ppColorUrls[i])
						.addParameter(PP_SET_URLS, xml.ATTR_REFLIST, ppSetUrls[i])
						.addParameter(PP_RESOLVED_SET_ITEM_PRODUCT_URL, xml.ATTR_REF, ppResolvedSetItemProductUrls[i])
						.addParameter(PP_RESOLVED_SET_ITEM_PRODUCTCOLOR_URL, xml.ATTR_REF, ppResolvedSetItemProductColorUrls[i]);
				}
			}

			response = await submitWriter(writer);
		}

		// step 3. go to next one
		runInAction(() => {
			if (response) {
				cache.cacheResponse(response);
				this._hasChanges = true;
			}

			if (!close) {
				// hit the last one, then back to the first one
				const next = store.getNextIdentity(activeCardUrl, false, true);
				this.setActiveCardUrl(next, true);

				// need to remove the resolved one from the list if no items skipped
				if (this._hasChanges && response && !keepViolation) {
					store.remove(activeCardUrl);
				}
			} else {
				reset();
			}
		});

		return this._hasChanges;
	};

	constructor(props: IPresentationProps, cache: ICache) {
		super();
		makeObservable(this);
		this.props = props;
		this.cache = cache;

		/** when the store is ready, select the first one by default */
		this.trackReaction(reaction(
			() => this.store?.get(0)?.$URL,
			(url) => {
				if (url) {
					this.setActiveCardUrl(url, true);
				}
			},
		));
	}
}

export default PPValidationStore;

export const PPValidationStoreContext = createContext<PPValidationStore>(undefined);
export const usePPValidationStore = (): PPValidationStore => useContext(PPValidationStoreContext);